import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { fmtDepth, fmtNum } from "@lib/format";
import type { Locale } from "@i18n/locales";
import { useGetBhaFullJobQuery } from "./bhaApi";

/**
 * Full-job BHA summary (§ full-job/bha) — the well-wide roll-up Ramon asked for:
 * a "summary by section" table (section / run count / footage / MD range) and a
 * "by Run" table across every event on the well. Every value is server-computed
 * by Oxbo-P; the FE only lays it out.
 */
export function BhaSummary({ wellUwi }: { wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetBhaFullJobQuery(wellUwi, {
    skip: !wellUwi,
  });

  const bySection = Array.isArray(data?.by_section) ? data!.by_section : [];
  const byRun = Array.isArray(data?.by_run) ? data!.by_run : [];

  const totalRuns = byRun.length;
  const totalFootage = bySection.reduce((s, r) => s + (r.footage ?? 0), 0);
  const totalBitOps = byRun.reduce((s, r) => s + (r.bit_op_count ?? 0), 0);

  const stats: Stat[] = data
    ? [
        { label: t("bha.kpi.runs"), value: fmtNum(totalRuns, locale) },
        { label: t("bhaSummary.kpi.footage"), value: fmtDepth(totalFootage, locale), tone: "ops" },
        { label: t("bha.kpi.bitOps"), value: fmtNum(totalBitOps, locale) },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && bySection.length === 0 && byRun.length === 0}
      emptyMessage={t("bhaSummary.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("bhaSummary.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      {/* Summary by section */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("bhaSummary.bySection")}</h3>
        {bySection.length === 0 ? (
          <div className="empty-state">{t("bhaSummary.noData")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("bhaSummary.col.section")}</th>
                  <th className="num">{t("bhaSummary.col.runCount")}</th>
                  <th className="num">{t("bhaSummary.col.footage")}</th>
                  <th className="num">{t("bhaSummary.col.mdRange")}</th>
                </tr>
              </thead>
              <tbody>
                {bySection.map((r, i) => (
                  <tr key={r.section ?? `sec-${i}`} data-owner="p">
                    <td>{r.section ?? t("common.na")}</td>
                    <td className="num tab-nums">{fmtNum(r.run_count, locale)}</td>
                    <td className="num tab-nums">{fmtDepth(r.footage, locale)}</td>
                    <td className="num tab-nums">{fmtMdRange(r.md_in_min, r.md_out_max, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* By Run */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("bhaSummary.byRun")}</h3>
        {byRun.length === 0 ? (
          <div className="empty-state">{t("bhaSummary.noData")}</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("bhaSummary.col.section")}</th>
                  <th>{t("bha.run.dates")}</th>
                  <th className="num">{t("bhaSummary.col.mdRange")}</th>
                  <th className="num">{t("bhaSummary.col.footage")}</th>
                  <th className="num">{t("bha.kpi.bitOps")}</th>
                </tr>
              </thead>
              <tbody>
                {byRun.map((r) => (
                  <tr key={r.id} data-owner="p">
                    <td>{r.section ?? t("common.na")}</td>
                    <td className="mono" style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                      {fmtDate(r.date_in, locale)} → {fmtDate(r.date_out, locale)}
                    </td>
                    <td className="num tab-nums">{fmtMdRange(r.md_in, r.md_out, locale)}</td>
                    <td className="num tab-nums">{fmtDepth(r.footage, locale)}</td>
                    <td className="num tab-nums">{fmtNum(r.bit_op_count, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </QueryState>
  );
}

/** "md_in / md_out" as depths; "—" when both are null. */
function fmtMdRange(a: number | null, b: number | null, locale: Locale): string {
  if (a == null && b == null) return "—";
  return `${fmtDepth(a, locale)} / ${fmtDepth(b, locale)}`;
}

/** A server date string (ISO date or datetime) in the tenant locale; "—" when null. */
function fmtDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}
